import { BaseEmailProvider } from '../BaseEmailProvider';
import type { EmailData, RegistrationNotificationData, EmailResponse } from '../types';

/**
 * Mailgun Email Provider
 */
export class MailgunProvider extends BaseEmailProvider {
  protected providerName = 'MAILGUN';

  /**
   * Validate Mailgun configuration
   */
  validateConfiguration(): boolean {
    const apiKey = import.meta.env.VITE_MAILGUN_API_KEY;
    const domain = import.meta.env.VITE_MAILGUN_DOMAIN;

    if (!apiKey || !domain) {
      return false;
    }

    // Basic format validation
    if (!apiKey.startsWith('key-')) {
      return false;
    }

    if (!domain.includes('.')) {
      return false;
    }

    return true;
  }

  /**
   * Send contact email via Mailgun API
   */
  async sendEmail(data: EmailData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'contact');

      if (!this.validateConfiguration()) {
        throw new Error('Mailgun configuration is invalid. Check your API key and domain.');
      }

      const emailContent = this.generateContactEmailContent(data);
      
      const result = await this.sendViaMailgunAPI({
        from: emailContent.fromEmail,
        to: emailContent.toEmail,
        subject: emailContent.subject,
        html: emailContent.htmlContent,
        text: emailContent.textContent,
        replyTo: data.userEmail
      });

      this.logEmailSuccess(result.messageId, emailContent.toEmail);

      return {
        success: true,
        message: 'Email successfully sent to Fabrik 28',
        messageId: result.messageId
      };

    } catch (error: any) {
      this.logEmailError(error, data.to);
      
      return {
        success: false,
        message: `Mailgun error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send registration notification via Mailgun API
   */
  async sendRegistrationNotification(data: RegistrationNotificationData): Promise<EmailResponse> {
    try {
      this.logEmailAttempt(data, 'registration');

      if (!this.validateConfiguration()) {
        throw new Error('Mailgun configuration is invalid. Check your API key and domain.');
      }

      const emailContent = this.generateRegistrationEmailContent(data);
      
      const result = await this.sendViaMailgunAPI({
        from: emailContent.fromEmail,
        to: emailContent.toEmail,
        subject: emailContent.subject,
        html: emailContent.htmlContent,
        text: emailContent.textContent,
        replyTo: data.userEmail
      });

      this.logEmailSuccess(result.messageId, emailContent.toEmail);

      return {
        success: true,
        message: 'Registration notification sent successfully via Mailgun',
        messageId: result.messageId
      };

    } catch (error: any) {
      this.logEmailError(error, this.getToEmail());
      
      return {
        success: false,
        message: `Mailgun error: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send email via Mailgun API
   */
  private async sendViaMailgunAPI(emailData: {
    from: string;
    to: string;
    subject: string;
    html: string;
    text: string;
    replyTo: string;
  }): Promise<{ messageId: string }> {
    const mailgunDomain = import.meta.env.VITE_MAILGUN_DOMAIN;
    const mailgunApiKey = import.meta.env.VITE_MAILGUN_API_KEY;
    
    const apiUrl = `https://api.mailgun.net/v3/${mailgunDomain}/messages`;
    
    // Format FROM email properly
    const formattedFromEmail = emailData.from.includes('<') 
      ? emailData.from 
      : `Fabrik 28 <${emailData.from}>`;
    
    // Prepare form data
    const formData = new FormData();
    formData.append('from', formattedFromEmail);
    formData.append('to', emailData.to);
    formData.append('subject', emailData.subject);
    formData.append('html', emailData.html);
    formData.append('text', emailData.text);
    formData.append('h:Reply-To', emailData.replyTo);
    
    // Prepare authentication
    const authString = `api:${mailgunApiKey}`;
    const authHeader = `Basic ${btoa(authString)}`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
      },
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Mailgun API error: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    
    return {
      messageId: result.id || 'unknown'
    };
  }
}
